/**
 * Certificates API Service
 * This service handles communication with the Azure Functions backend for certificates functionality.
 */

import axios from 'axios';

class CertificatesService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE || 'http://localhost:7071/api';
    // Ensure baseURL doesn't end with slash to avoid double slashes
    this.baseURL = this.baseURL.endsWith('/') ? this.baseURL.slice(0, -1) : this.baseURL;
    console.log('CertificatesService initialized with baseURL:', this.baseURL);
  }

  /**
   * Get all certificates for a user
   * @param {string} userId - User ID
   * @returns {Promise} - API response with certificates
   */
  async getUserCertificates(userId) {
    try {
      const url = `${this.baseURL}/certificates/get?userId=${userId}`;
      console.log(`Getting certificates from: ${url}`);
      
      const response = await axios.get(url);
      
      if (response.data && response.data.data) {
        return response.data;
      }
      
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error fetching certificates:', error);
      throw error;
    }
  }

  /**
   * Generate a certificate for course completion
   * @param {Object} certificateData - Certificate data object
   * @returns {Promise} - API response
   */
  async generateCertificate(certificateData) {
    try {
      const url = `${this.baseURL}/certificates/post`;
      console.log(`Generating certificate: ${url}`, certificateData);
      
      const response = await axios.post(url, certificateData);
      
      if (response.data && response.data.data) {
        return response.data;
      }
      
      return { data: response.data, message: "Certificate generated successfully" };
    } catch (error) {
      console.error('Error generating certificate:', error);
      throw error;
    }
  }

  /**
   * Update a certificate
   * @param {string} userId - User ID
   * @param {string} docId - Document ID
   * @param {Object} updateData - Updated certificate data
   * @returns {Promise} - API response
   */
  async updateCertificate(userId, docId, updateData) {
    try {
      const url = `${this.baseURL}/certificates/update`;
      console.log(`Updating certificate: ${url}`, { userId, docId, ...updateData });
      
      const response = await axios.put(url, { userId, docId, ...updateData });
      
      return response.data || { message: "Certificate updated successfully" };
    } catch (error) {
      console.error('Error updating certificate:', error);
      throw error;
    }
  }

  /**
   * Delete a certificate
   * @param {string} userId - User ID
   * @param {string} docId - Document ID
   * @returns {Promise} - API response
   */
  async deleteCertificate(userId, docId) {
    try {
      const url = `${this.baseURL}/certificates/delete`;
      console.log(`Deleting certificate: ${url}`);
      
      const response = await axios.delete(url, {
        data: { userId, docId }
      });
      
      return response.data || { message: "Certificate deleted successfully" };
    } catch (error) {
      console.error('Error deleting certificate:', error);
      throw error;
    }
  }

  /**
   * Delete all certificates for a user
   * @param {string} userId - User ID
   * @returns {Promise} - API response
   */
  async deleteAllCertificates(userId) {
    try {
      const url = `${this.baseURL}/certificates/deleteall`;
      console.log(`Deleting all certificates: ${url}`);
      
      const response = await axios.delete(url, {
        data: { userId }
      });
      
      return response.data || { message: "All certificates deleted successfully" };
    } catch (error) {
      console.error('Error deleting all certificates:', error);
      throw error;
    }
  }

  /**
   * Check if user has earned a certificate for a course
   * @param {string} userId - User ID
   * @param {string} courseId - Course ID
   * @returns {Promise<boolean>} - True if certificate exists
   */
  async hasCertificateForCourse(userId, courseId) {
    try {
      const response = await this.getUserCertificates(userId);
      const certificates = response.data || [];
      
      return certificates.some(cert => cert.courseId === courseId && cert.status === 'active');
    } catch (error) {
      console.error('Error checking certificate status:', error);
      return false;
    }
  }

  /**
   * Request certificate generation when requirements are met
   * @param {string} userId - User ID
   * @param {string} courseId - Course ID
   * @param {Object} completionData - Course completion data
   * @returns {Promise} - API response
   */
  async requestCertificate(userId, courseId, completionData) {
    try {
      const certificateData = {
        userId,
        courseId,
        ...completionData,
        requestedAt: new Date().toISOString()
      };

      return await this.generateCertificate(certificateData);
    } catch (error) {
      console.error('Error requesting certificate:', error);
      throw error;
    }
  }
}

const certificatesService = new CertificatesService();
export default certificatesService;
